import { hasValue } from 'src/packages/utils/has-value';

import type {
  IDirectoriesStorage,
  TStorageObject,
} from 'src/services/directories-storage-service/directories-storage-service';
import type { TDirectorySource, TValueSourceObject } from 'src/services/directory-service/types';

import { compareDataWithFilterItems } from './compare-data-with-filter-items';
import { mapFilterRuleItems } from './map-filter-rule-items';

function binarySearch<A = number, T = number>(
  array: A[],
  target: T,
  params?: {
    getCurrentComparingValue?: (v: A) => number;
    getTargetComparingValue?: (v: T) => number;
  },
): { element: A | null; index: number } {
  let left = 0;
  let right = array.length - 1;

  let element: A | null = null;
  let index = -1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const current = array[mid];

    const currentValue = params?.getCurrentComparingValue?.(current) ?? current;
    const targetValue = params?.getTargetComparingValue?.(target) ?? target;

    if (currentValue === targetValue) {
      element = current;
      index = mid;
      break;
    }

    if (currentValue < targetValue) {
      left = mid + 1;
      continue;
    }

    if (currentValue > targetValue) {
      right = mid - 1;
    }
  }

  return {
    element,
    index,
  };
}

export const handleValueSourceRule = (
  directoriesStorageService: IDirectoriesStorage,
  record: Record<string, unknown>,
  sources: TValueSourceObject | TValueSourceObject[],
): { value: unknown; valuesByPseudonims: Record<string, unknown> } => {
  const valuesByPseudonims: Record<string, unknown> = {};

  function processNestedSource(sourceObj: TDirectorySource, value: unknown): unknown {
    const directory = directoriesStorageService.directories[sourceObj.sourceDirectory];

    if (!directory) {
      console.error(`cannot find corresponding directory with given directory name ${sourceObj.sourceDirectory}`);
      return null;
    }

    const filters = sourceObj.filter ? mapFilterRuleItems(sourceObj.filter, directoriesStorageService, record) : [];

    const filteredDirectory = !!filters.length
      ? directory.filter((d) => {
          const { data, ...rest } = d;
          const dirData: Record<string, unknown> = { ...rest, ...data };

          return compareDataWithFilterItems(dirData, filters);
        })
      : directory;

    const processDirectoryRecord = (directoryRecord: TStorageObject) => {
      const recordValue = (() => {
        if (sourceObj.source === 'id') {
          return directoryRecord.id;
        }

        return sourceObj.source ? directoryRecord.data[sourceObj.source] : directoryRecord;
      })();

      if (sourceObj.pseudonim) {
        valuesByPseudonims[sourceObj.pseudonim] = recordValue;
      }

      if (sourceObj.additionalData) {
        sourceObj.additionalData.forEach((ad) => {
          const adRecordValue = (() => {
            if (ad.source === 'id') {
              return directoryRecord.id;
            }

            return ad.source ? directoryRecord.data[ad.source] : directoryRecord;
          })();

          valuesByPseudonims[ad.pseudonim] = adRecordValue;
        });
      }

      if (!hasValue(recordValue)) {
        return null;
      }

      return sourceObj.isSourceFor ? processNestedSource(sourceObj.isSourceFor, recordValue) : recordValue;
    };

    if (Array.isArray(value)) {
      const filteredResult: unknown[] = [];

      for (const valueItem of value) {
        const directoryRecord = (() => {
          if (typeof valueItem === 'object' && valueItem !== null) {
            return valueItem;
          }

          if (sourceObj.compareWith) {
            return filteredDirectory.find((d) => d.data[sourceObj.compareWith!] === valueItem);
          } else {
            const { element, index } = binarySearch(filteredDirectory, valueItem, {
              getCurrentComparingValue(v) {
                return v.id;
              },
            });

            return element;
          }
        })() as TStorageObject;

        if (!directoryRecord) {
          continue;
        }

        filteredResult.push(processDirectoryRecord(directoryRecord));
      }

      return filteredResult;
    } else {
      const directoryRecord = (() => {
        if (typeof value === 'object' && value !== null) {
          return value;
        }

        if (sourceObj.compareWith) {
          return filteredDirectory.find((d) => d.data[sourceObj.compareWith!] === value);
        } else {
          const { element, index } = binarySearch(filteredDirectory, value, {
            getCurrentComparingValue(v) {
              return v.id;
            },
          });

          return element;
        }
      })() as TStorageObject;

      if (!directoryRecord) {
        return null;
      }

      return processDirectoryRecord(directoryRecord);
    }
  }

  function getValueFromSources(sourceObj: TValueSourceObject): unknown {
    const filters = sourceObj.filter ? mapFilterRuleItems(sourceObj.filter, directoriesStorageService, record) : [];

    if (sourceObj.sourceType === 'directory') {
      if (sourceObj.isConstDirectory) {
        // добавить поддержку директорий-констант
        console.warn('константные справочники пока что не поддерживаются');
        return null;
      }

      const directory = directoriesStorageService.directories[sourceObj.sourceDirectory];

      if (!directory) {
        console.error('не удалось найти директорию', sourceObj.sourceDirectory);
        return null;
      }

      const value = (() => {
        if (sourceObj.source) {
          const filteredDirectoryValues: unknown[] = [];

          const source = sourceObj.source;

          directory.forEach((d) => {
            const { data, ...rest } = d;
            const dirData: Record<string, unknown> = { ...rest, ...data };

            if (compareDataWithFilterItems(dirData, filters)) {
              filteredDirectoryValues.push(dirData[source]);
            }
          });

          return filteredDirectoryValues;
        }

        return directory.filter((d) => {
          const { data, ...rest } = d;
          const dirData: Record<string, unknown> = { ...rest, ...data };

          return compareDataWithFilterItems(dirData, filters);
        });
      })();

      return sourceObj.isSourceFor ? processNestedSource(sourceObj.isSourceFor, value) : value;
    } else {
      const value = record[sourceObj.source];

      if (sourceObj.pseudonim) {
        valuesByPseudonims[sourceObj.pseudonim] = value;
      }

      if (sourceObj.additionalData) {
        sourceObj.additionalData.forEach((ad) => {
          valuesByPseudonims[ad.pseudonim] = record[ad.source];
        });
      }

      if (!hasValue(value)) {
        return null;
      }

      return sourceObj.isSourceFor ? processNestedSource(sourceObj.isSourceFor, value) : value;
    }
  }

  if (!Array.isArray(sources)) {
    return {
      value: getValueFromSources(sources),
      valuesByPseudonims,
    };
  } else {
    return {
      value: sources.map((source) => getValueFromSources(source)),
      valuesByPseudonims,
    };
  }
};
